import { WebPageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import { endOfDay } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import { TaskCreateLinkedItemSelect } from 'shared/components/ui/task-create-linked-item-select';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { Goal } from 'shared/types/goal';
import { LifeArea } from 'shared/types/life-area';
import {
  OnboardingGoal,
  OnboardingLocalizedLifeArea,
} from 'shared/types/onboarding-answers';
import { MinimalNewTask } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';

import { OnboardingHeader } from '../onboarding-header';
import { ProgressBar } from '../progress-bar';
import * as Styled from './tasks.style';

const TASKS_IMAGE_URL = '/images/onboarding/task-manager.png';

type LinkedItem = {
  lifeAreaId: string | null;
  goalId: string | null;
};

const OnboardingRightPanelImage: React.FC<{ imageUrl: string }> = ({
  imageUrl,
}) => (
  <Styled.RightPanel>
    <Styled.RightPanelImage src={imageUrl} alt="App preview" />
  </Styled.RightPanel>
);

export type TasksProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
  onSubmit: (tasks: MinimalNewTask[]) => void;
  goals: OnboardingGoal[];
  lifeAreas: OnboardingLocalizedLifeArea[];
};

export const Tasks: React.FC<TasksProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
  onSubmit,
  goals,
  lifeAreas,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingTasks, undefined);
  const { t } = useTranslation();
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const [tasks, setTasks] = useState(['', '', '']);
  const [selectedLinkedItems, setSelectedLinkedItems] = useState<
    (LinkedItem | undefined)[]
  >([undefined, undefined, undefined]);
  const suggestions = useMemo(
    () => [
      t('forms.onboarding.tasks.suggestions.1'),
      t('forms.onboarding.tasks.suggestions.2'),
      t('forms.onboarding.tasks.suggestions.3'),
      t('forms.onboarding.tasks.suggestions.4'),
      t('forms.onboarding.tasks.suggestions.5'),
      t('forms.onboarding.tasks.suggestions.6'),
      t('forms.onboarding.tasks.suggestions.7'),
    ],
    [t],
  );

  const unusedSuggestions = useMemo(
    () => suggestions.filter((s) => !tasks.includes(s)),
    [suggestions, tasks],
  );

  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window;
    if (firstInputRef.current && !isTouchDevice) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleTaskChange = (index: number, value: string) => {
    const newTasks = [...tasks];
    newTasks[index] = value;
    setTasks(newTasks);
  };

  const handleLinkedItemChange = (index: number, linkedItem: LinkedItem) => {
    const newLinkedItems = [...selectedLinkedItems];
    newLinkedItems[index] = linkedItem;
    setSelectedLinkedItems(newLinkedItems);
  };

  const handleSuggestionClick = (taskText: string) => {
    const firstEmptyIndex = tasks.findIndex((task) => !task);
    if (firstEmptyIndex !== -1) {
      handleTaskChange(firstEmptyIndex, taskText);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(
      tasks
        .map((task, index) => ({
          name: task,
          type: TaskType.Task as TaskType.Task,
          endStrategy: {
            deadline: endOfDay(new Date()),
            completionCount: null,
          },
          ...(selectedLinkedItems[index] ?? {}),
        }))
        .filter(({ name }) => !!name),
    );
    nextStep();
  };

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />
        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Form id="tasks-form" onSubmit={handleSubmit}>
            <Styled.Content>
              <Typography variant="h4" component="h1">
                {t('forms.onboarding.tasks.title')}
              </Typography>
              <Styled.Subtitle>
                {t('forms.onboarding.tasks.subtitle')}
              </Styled.Subtitle>
            </Styled.Content>

            <Styled.TasksContainer>
              {tasks.map((task, index) => (
                <Styled.TaskRow key={index}>
                  <Styled.Input
                    ref={index === 0 ? firstInputRef : null}
                    placeholder={t('forms.onboarding.tasks.placeholder')}
                    value={task}
                    onChange={(e) => handleTaskChange(index, e.target.value)}
                  />

                  <Styled.TaskRowControl>
                    <TaskCreateLinkedItemSelect
                      onChange={(linkedItem) =>
                        handleLinkedItemChange(index, linkedItem)
                      }
                      lifeAreaId={selectedLinkedItems[index]?.lifeAreaId}
                      goalId={selectedLinkedItems[index]?.goalId}
                      goals={goals as Goal[]}
                      lifeAreas={lifeAreas as LifeArea[]}
                      onboarding
                    />
                  </Styled.TaskRowControl>
                </Styled.TaskRow>
              ))}
            </Styled.TasksContainer>

            <Styled.ChipsRow>
              {unusedSuggestions.map((task) => (
                <Styled.Chip
                  key={task}
                  type="button"
                  onClick={() => handleSuggestionClick(task)}
                >
                  <Icon icon={Plus} />
                  <Styled.ChipLabel>{task}</Styled.ChipLabel>
                </Styled.Chip>
              ))}
            </Styled.ChipsRow>
          </Styled.Form>

          <Styled.FooterArea>
            <Button
              type="submit"
              form="tasks-form"
              size={Sizes.Medium}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.continue.label')}
            </Button>
            <Button
              type="button"
              onClick={nextStep}
              variant={Variants.Text}
              size={Sizes.Medium}
              color={Colors.Inherit}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.skip.label')}
            </Button>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <OnboardingRightPanelImage imageUrl={TASKS_IMAGE_URL} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
